import { BilberryWidgetsGlobal } from '@repo/types';
import { BASE_SERVICE_URL, USE_SERVICE_PROXY } from '@repo/widget-env/__autogen/env';
import { apiError } from './ApiError';
import { token, apiUrl as membershipApiUrl } from '../fetchers/membership';

declare global {
    interface Window {
        BilberryWidgetsGlobal?: BilberryWidgetsGlobal;
    }
}

async function executeRequest(
    url: string,
    options: RequestInit,
    timeout = 60000,
    parseJsonResponse = true,
) {
    const controller = new AbortController();
    const timer = setTimeout(() => {
        controller.abort(`Request reached timeout (${timeout / 1000} seconds): ${url}`);
    }, timeout);
    const isMembershipUrl = url.startsWith(membershipApiUrl());

    // Dev-only: if `BilberryWidgetsGlobal.devProxyTarget` is set, tag service-API
    // (non-membership) requests with `X-Proxy-Target` so the widget app's Vite
    // dev proxy forwards them to that upstream. Production tenants never set
    // `devProxyTarget`, so this collapses to a no-op outside of dev. Use the
    // `Headers` API (rather than a plain-object spread) because `options.headers`
    // is a `Headers` instance from `getRequest()`, and `{...headersInstance}`
    // doesn't copy the entries.
    const devProxyTarget =
        typeof window !== 'undefined' ? window.BilberryWidgetsGlobal?.devProxyTarget : undefined;
    const shouldAttachProxyHeader =
        !isMembershipUrl && typeof devProxyTarget === 'string' && devProxyTarget.length > 0;
    let proxiedHeaders: Headers | undefined;
    if (shouldAttachProxyHeader) {
        proxiedHeaders = new Headers(options.headers);
        proxiedHeaders.set('X-Proxy-Target', devProxyTarget as string);
    }

    const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        // If this request is for membership-api url, add some headers.
        ...(isMembershipUrl && {
            headers: {
                ...options.headers,
                ...(token && { Authorization: `Bearer ${token}` }),
                'Content-type': 'application/json',
                Accept: 'application/json',
            },
        }),
        ...(proxiedHeaders && { headers: proxiedHeaders }),
    });

    clearTimeout(timer);

    if (!response.ok) {
        throw await apiError(response, isMembershipUrl);
    }

    return parseJsonResponse ? await response.json() : response;
}

export async function get<T>(fullUrl: string, headers?: Headers, timeout?: number): Promise<T> {
    const options: RequestInit = {
        method: 'GET',
        headers,
    };

    return await executeRequest(fullUrl, options, timeout);
}

export async function post(
    url: string,
    data?: any,
    headers?: Headers,
    timeout?: number,
    parseJsonResponse = true,
) {
    const options: RequestInit = {
        method: 'POST',
        headers,
        ...(data && { body: JSON.stringify(data) }),
    };

    return await executeRequest(url, options, timeout, parseJsonResponse);
}

export async function patch(
    url: string,
    data: any,
    headers?: Headers,
    timeout?: number,
    parseJsonResponse = true,
) {
    const options: RequestInit = {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
    };

    return await executeRequest(url, options, timeout, parseJsonResponse);
}

export async function put(
    url: string,
    data: any,
    headers?: Headers,
    timeout?: number,
    parseJsonResponse = true,
) {
    const options: RequestInit = {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
    };

    return await executeRequest(url, options, timeout, parseJsonResponse);
}

export async function del(
    url: string,
    data?: any,
    headers?: Headers,
    timeout?: number,
    parseJsonResponse = true,
) {
    const options: RequestInit = {
        method: 'DELETE',
        headers,
        ...(data && { body: JSON.stringify(data) }),
    };

    return await executeRequest(url, options, timeout, parseJsonResponse);
}

export function getUrlWithParams(
    url: string,
    locale: string,
    queryParams: Record<string, any> = {},
    config?: BilberryWidgetsGlobal,
) {
    const langQueryParam = `lang=${getBilberryLanguageFromLocale(locale)}`.toLowerCase();

    if (config && config.referralCode) {
        queryParams.bref = config.referralCode;
    }

    if (config && config.enableGodMode) {
        queryParams['god-mode'] = 1;
    }

    let fullUrl = `${url}?${langQueryParam}`;

    const queryString = new URLSearchParams(queryParams).toString();
    if (queryString.length > 0) fullUrl += `&${queryString}`;
    return fullUrl;
}

export function getBilberryLanguageFromLocale(locale: string) {
    return locale.split('-')[0];
}

export async function wait(ms: number) {
    return await new Promise((res) => {
        setTimeout(() => res(true), ms);
    });
}

function getApiBaseUrl(config: BilberryWidgetsGlobal) {
    const baseUrlFromGlobal = config.bilberryBaseApiUrl;
    if (baseUrlFromGlobal) return baseUrlFromGlobal;

    // Needed for local development through proxy
    return USE_SERVICE_PROXY ? '' : BASE_SERVICE_URL;
}

function getApiAccessToken(config: BilberryWidgetsGlobal) {
    return config.bilberryAccessToken;
}

function getApiHeaders(config: BilberryWidgetsGlobal, additionalHeaders: Record<string, any> = {}) {
    const accessToken = getApiAccessToken(config);

    const headers = new Headers({
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
    });

    Object.entries(additionalHeaders).forEach(([key, val]) => {
        if (val !== undefined) {
            headers.set(key, val);
        }
    });

    return headers;
}

export function getRequest(
    subdirectory: string,
    locale: string,
    config: BilberryWidgetsGlobal,
    queryParams: Record<string, any> = {},
    additionalHeaders?: Record<string, any>,
) {
    const endpoint = `${getApiBaseUrl(config)}${subdirectory}`;
    const sanitizedQueryParams = Object.entries(queryParams).reduce((acc, [key, val]) => {
        if (val !== undefined) {
            acc[key] = val;
        }
        return acc;
    }, {} as any);
    const fullUrl = getUrlWithParams(endpoint, locale, sanitizedQueryParams, config);

    const apiHeaders = getApiHeaders(config, additionalHeaders);
    apiHeaders.append('Accept-Language', locale);

    return {
        url: fullUrl,
        headers: apiHeaders,
    };
}
